
import { WrapResponse } from '@/types';
import { fun } from '@/lib/request';
import { StateSlice } from '@/stores/appStore';
import { isEqual, snakeToCamel, transferCamelToSnake } from '@/lib/utils';
import { toast } from 'react-hot-toast';
import { ID, Paper, PaperId } from '@/stores/slices/paperSlice';
import { shallow } from 'zustand/shallow';
import { api } from '@/lib/env';

// 0待处理，1处理中，2完成，-1失败
export type ParseStatus = 0 | 1 | 2 | -1 | -30010;

export type PaperItem = {
  // 0未删除，1已删除
  isDeleted: 0 | 1;
} & Pick<Paper, 'paperId' | 'paperName' | 'citationFormat' | 'error' | 'quote' | 'extractContent' | 'extra'>;
export type Summarize = {
  id: string;
  subject: string;
  summaryContent: string;
  status?: ParseStatus;
  schedule?: string;
  createUser?: number;
  createTime?: string;
  startTime?: string;
  finishTime?: string;
  paperList: PaperItem[];
  contentLength: number;
}

const namespace = 'summarizes';
export type SummarizeSlice = {
  [namespace]: {
    all: Summarize[]; // entities
    one: null | Summarize; // current
    fetching: boolean;
    silentFetching: boolean;
    setFetching: (loading: boolean, silent: boolean) => void;
    fetch: (silent?: boolean) => Promise<boolean>;
    fetchOne: (id: string) => Promise<boolean>;
    creating: boolean;
    create: (o: {
      [x: string]: string | string[];
      paperIds: string[];
    }) => Promise<null | Summarize>;
    createErrorMsg: string;
    setCreateErrorMsg: (createErrorMsg: string) => void;
    remove: (ids: string[]) => Promise<boolean>;
  }
};

let firstFetched = false;
export const createSummarizeSlice: StateSlice<SummarizeSlice> = (set, _get) => {
  const get = () => _get()[namespace];
  return {
    [namespace]: {
      all: [],
      one: null,
      fetching: false,
      silentFetching: false,
      setFetching: (loading, silent = false) => {
        set(({ [namespace]: state }) => {
          if (!silent) {
            state.fetching = loading;
          } else {
            state.silentFetching = loading;
          }
        });
      },
      fetch: async (silent = false) => {
        const { setFetching, fetching, silentFetching } = get();
        if (firstFetched) {
          // 已经在请求中了忽略
          if (fetching || silentFetching) {
            return true;
          }
        } else {
          firstFetched = true;
        }
        setFetching(true, silent);
        try {
          const { data, code, message } = (
            await fun.post<WrapResponse<Summarize[]>>(
              api('/workflow/main/list', true),
            )
          ).data;
          const summarizes = snakeToCamel<Summarize[]>(data);
          if (code === 0) {
            const all = summarizes.map(({ id, ...rest }) => ({
              id: id.toString(),
              ...rest
            }));
            if (!shallow(all, get().all)) {
              set(({ [namespace]: state }) => {
                state.all = all;
              });
            }
            return true;
          }
          toast.error(message);
          return false;
        } catch (error) {
          console.error('Error fetch paper summary:', error);
          return false;
        } finally {
          setFetching(false, silent);
        }
      },
      fetchOne: async (id: string) => { return true; },
      creating: false,
      create: async ({
        paperIds,
        ...contents
      }) => {
        set(({ [namespace]: state }) => {
          state.creating = true;
        });
        try {
          const formData = new FormData();
          paperIds.forEach((paperId, i) => {
            formData.append(`paper_id[${i}]`, paperId);
          });
          formData.append(`platform`, 'web');
          Object.keys(contents).forEach((k) => {
            const key = transferCamelToSnake(k) as string;
            const val = contents[k as (keyof typeof contents)] as string;
            if (key && val) {
              formData.append(key, val);
            }
          })
          const { data, code, message: msg } = (
            await fun.post<WrapResponse<Summarize>>(
              api('/paper/summary/create', true),
              formData,
            )
          ).data;
          if (code === 0) {
            const { id, ...rest } = snakeToCamel<Summarize>(data);
            const summarize = id ? {
              id: id.toString(),
              ...rest
            } : null;
            set(({ [namespace]: state }) => {
              state.one = summarize;
            });
            _get().refreshUserInfo();
            return summarize;
          } else if (code === 5001) {
            _get().setBalanceInsufficient(true);
          } else {
            // toast.error(msg);
            get().setCreateErrorMsg(msg);
          }
          console.error('Error create summarize:', msg);
        } catch (error) {
          console.error('Error create summarize:', error);
        } finally {
          set(({ [namespace]: state }) => {
            state.creating = false;
          });
        }
        return null;
      },
      createErrorMsg: '',
      setCreateErrorMsg: (createErrorMsg: string) => {
        set(({ [namespace]: state }) => {
          state.createErrorMsg = createErrorMsg;
        });
      },
      remove: async (ids) => {
        try {
          const formData = new FormData();
          ids.forEach((id, i) => {
            formData.append(`id[${i}]`, id);
          })
          const { code, message } = (await fun.post<WrapResponse<{}>>(
            api('/paper/summary/delete', true), formData
          )).data;
          if (code === 0) {
            set(({ [namespace]: state }) => {
              state.all = state.all.filter((summarize) => !ids.includes(summarize.id));
            });
            return true;
          } else {
            toast.error(message);
          }
          return false;
        } catch (error) {
          console.error('Error delete summarize:', error);
          return false;
        } finally {

        }
      },
    },
  };
};